import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import { periodRange, secondsToHours } from '../lib/formatHelpers';

/**
 * useAdminPilotageData — fetches and aggregates data for the admin pilotage page.
 *
 * Sources (all Phase 1 / Excel-injected, no scraping) :
 *  - public.creators           : roster + status + tier_current + join_time
 *  - public.creator_metrics_daily : per-day metrics (sessions, live_streams,
 *                                   live_duration_seconds, valid_go_live_days,
 *                                   matches, new_followers) for the period
 *  - public.creator_current_month : last-known per-creator snapshot
 *
 * The hook applies multi-tenant filtering (tenant_id + agency_id) per R1.
 * Pagination (R10) : creator_metrics_daily is fetched in pages of 1000 rows.
 *
 * Returns :
 *  - hero       : { activeCreators, totalCreators, sessionsCount, hoursTotal,
 *                   validGoLiveDays, newRecruits }
 *  - indicators : { uniqueCreators, newFollowers, matches, goLiveRate,
 *                   avgSessions, avgHours }
 *  - tableData  : array<{ creator_id, display_name, tiktok_username, server,
 *                         tier_current, sessions, hours, days, status }>
 *  - loading
 *  - error
 *  - reload()
 */
export function useAdminPilotageData({ period = 'month', server = null } = {}) {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [creators, setCreators] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [currentMonth, setCurrentMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const range = periodRange(period);

      // 1) Creators roster (paginated, single agency or all under tenant)
      let q1 = supabase
        .from('black_creators')
        .select('id, display_name, tiktok_username, server, status, tier, join_time, agency_id, manager_id, tenant_id')
        .eq('tenant_id', tenantId);
      if (agencyId) q1 = q1.eq('agency_id', agencyId);
      const { data: creatorsData, error: e1 } = await q1.limit(1000);
      if (e1) throw e1;

      // 2) creator_metrics_daily over period (paginated R10)
      const PAGE = 1000;
      const allDaily = [];
      let from = 0;
      // Loop until under-full page
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let q = supabase
          .from('black_creator_daily')
          .select('creator_id, snapshot_date, live_streams, live_duration_seconds, valid_go_live_days, matches, new_followers')
          .eq('tenant_id', tenantId)
          .gte('snapshot_date', range.start)
          .lte('snapshot_date', range.end);
        if (agencyId) q = q.eq('agency_id', agencyId);
        const { data, error: e2 } = await q.range(from, from + PAGE - 1);
        if (e2) throw e2;
        const rows = data || [];
        allDaily.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }

      // 3) creator_current_month for tier + identity (used in tableData fallback)
      let q3 = supabase
        .from('black_creator_current_month')
        .select('creator_id, tier_current, tier_last_month, status, days_since_joining, server')
        .eq('tenant_id', tenantId);
      if (agencyId) q3 = q3.eq('agency_id', agencyId);
      const { data: currentMonthData, error: e3 } = await q3.limit(1000);
      if (e3) throw e3;

      setCreators((creatorsData || []).filter((c) => !server || c.server === server));
      setDailyRows(allDaily);
      setCurrentMonth(currentMonthData || []);
    } catch (e) {
      console.error('[useAdminPilotageData] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, period, server]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  // Aggregate per-creator activity for the period
  const perCreator = useMemo(() => {
    const map = new Map();
    for (const row of dailyRows) {
      const cur = map.get(row.creator_id) || {
        sessions: 0,
        hours: 0,
        days: 0,
        validGoLiveDays: 0,
        matches: 0,
        newFollowers: 0,
      };
      cur.sessions += row.live_streams || 0;
      cur.hours += secondsToHours(row.live_duration_seconds || 0);
      cur.days += row.valid_go_live_days > 0 ? 1 : 0;
      cur.validGoLiveDays += row.valid_go_live_days || 0;
      cur.matches += row.matches || 0;
      cur.newFollowers += row.new_followers || 0;
      map.set(row.creator_id, cur);
    }
    return map;
  }, [dailyRows]);

  const hero = useMemo(() => {
    const range = periodRange(period);
    const startDate = new Date(range.start);
    let sessions = 0, hours = 0, validDays = 0, newRecruits = 0, activeCount = 0;
    for (const c of creators) {
      const agg = perCreator.get(c.id);
      if (agg) {
        sessions += agg.sessions;
        hours += agg.hours;
        validDays += agg.validGoLiveDays;
        if (agg.sessions > 0 || agg.validGoLiveDays > 0) activeCount += 1;
      }
      if (c.join_time) {
        const j = new Date(c.join_time);
        if (j >= startDate) newRecruits += 1;
      }
    }
    return {
      activeCreators: activeCount,
      totalCreators: creators.length,
      sessionsCount: sessions,
      hoursTotal: hours,
      validGoLiveDays: validDays,
      newRecruits,
    };
  }, [creators, perCreator, period]);

  const indicators = useMemo(() => {
    let unique = 0, newFollowers = 0, matches = 0, totalSessions = 0, totalHours = 0;
    for (const c of creators) {
      const agg = perCreator.get(c.id);
      if (agg) {
        if (agg.sessions > 0) unique += 1;
        newFollowers += agg.newFollowers;
        matches += agg.matches;
        totalSessions += agg.sessions;
        totalHours += agg.hours;
      }
    }
    const goLiveRate = creators.length > 0 ? (unique / creators.length) * 100 : null;
    const avgSessions = unique > 0 ? totalSessions / unique : null;
    const avgHours = unique > 0 ? totalHours / unique : null;
    return {
      uniqueCreators: unique,
      newFollowers,
      matches,
      goLiveRate,
      avgSessions,
      avgHours,
    };
  }, [creators, perCreator]);

  const tableData = useMemo(() => {
    const cmByCreator = new Map(currentMonth.map((cm) => [cm.creator_id, cm]));
    return creators.map((c) => {
      const agg = perCreator.get(c.id) || { sessions: 0, hours: 0, days: 0 };
      const cm = cmByCreator.get(c.id);
      return {
        creator_id: c.id,
        display_name: c.display_name,
        tiktok_username: c.tiktok_username,
        server: c.server || cm?.server,
        tier_current: cm?.tier_current ?? c.tier,
        sessions: agg.sessions,
        hours: agg.hours,
        days: agg.days,
        status: c.status || cm?.status,
      };
    });
  }, [creators, currentMonth, perCreator]);

  return {
    hero,
    indicators,
    tableData,
    loading: loading || tenantLoading,
    error,
    reload: fetchAll,
  };
}
